// assets/ud-plp-bundle-grid.js
import { CartAddEvent } from '@theme/events';

class UdPlpBundleGrid extends HTMLElement {
  connectedCallback() {
    this.qty = new Map();
    this.money = new Intl.NumberFormat(this.dataset.locale || 'en-US', {
      style: 'currency',
      currency: this.dataset.currency || 'USD',
    });

    this.packs = [...this.querySelectorAll('[data-ud-pack]')];
    this.cards = [...this.querySelectorAll('[data-ud-item]')];
    this.cta = this.querySelector('[data-ud-cta]');
    this.modal = this.querySelector('[data-ud-modal]');
    this.pack = this.packs.find((pack) => pack.dataset.default === 'true') || this.packs[0];

    this.addEventListener('click', this.onClick);
    document.addEventListener('keydown', this.onKeydown);
    this.observeBar();
    this.render();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    document.removeEventListener('keydown', this.onKeydown);
    this.observer?.disconnect();
  }

  get total() {
    let sum = 0;
    this.qty.forEach((value) => (sum += value));
    return sum;
  }

  get limit() {
    return Number(this.pack?.dataset.count || 0);
  }

  get ctaLabel() {
    return this.cta.querySelector('[data-ud-cta-text]') || this.cta;
  }

  get sellerId() {
    if (this.dataset.attachSellerId !== 'true') return '';
    const hero = document.querySelector('[data-ud-seller-id]');
    return this.dataset.sellerIdOverride || hero?.dataset.udSellerId || '';
  }

  observeBar() {
    if (this.dataset.barVisibility !== 'inview') return;
    const grid = this.querySelector('[data-ud-grid]');
    if (!grid) return;

    this.observer = new IntersectionObserver(
      ([entry]) => this.toggleAttribute('data-ud-bar-visible', entry.isIntersecting),
      { rootMargin: '0px 0px -15% 0px' }
    );
    this.observer.observe(grid);
  }

  onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const step = target.closest('[data-ud-step]');
    if (step) return this.step(step);

    const pack = target.closest('[data-ud-pack]');
    if (pack) return this.selectPack(pack);

    if (target.closest('[data-ud-modal-close]')) return this.toggleModal(false);
    if (target.closest('[data-ud-cta]')) return this.submit();
  };

  onKeydown = (event) => {
    if (event.key === 'Escape') this.toggleModal(false);
  };

  step(button) {
    const card = button.closest('[data-ud-item]');
    if (!card) return;

    const id = card.dataset.variantId;
    const current = this.qty.get(id) || 0;
    const next = current + Number(button.dataset.udStep);
    if (next < 0 || (next > current && this.total >= this.limit)) return;

    next ? this.qty.set(id, next) : this.qty.delete(id);
    this.render();
  }

  selectPack(pack) {
    if (Number(pack.dataset.count) < this.total) return this.openReduceModal(pack);
    this.pack = pack;
    this.render();
  }

  openReduceModal(pack) {
    const body = this.modal?.querySelector('[data-ud-modal-body]');
    if (body) {
      body.textContent = (this.modal.dataset.bodyTemplate || '')
        .replaceAll('{pack}', pack.dataset.label || '')
        .replaceAll('{count}', String(this.total));
    }
    this.toggleModal(true);
  }

  toggleModal(open) {
    if (!this.modal) return;
    this.modal.hidden = !open;
    document.body.style.overflow = open ? 'hidden' : '';
    if (open) this.modal.querySelector('.ud-plp-bundle__modal-button')?.focus();
  }

  render() {
    const { total, limit } = this;
    const summary = [];
    let sum = 0;

    for (const card of this.cards) {
      const count = this.qty.get(card.dataset.variantId) || 0;
      const output = card.querySelector('[data-ud-qty]');
      if (output && output.textContent !== String(count)) output.textContent = String(count);

      card.toggleAttribute('data-ud-active', count > 0);

      const [minus, plus] = card.querySelectorAll('[data-ud-step]:not([data-ud-add])');
      if (minus) minus.disabled = count === 0;
      if (plus) plus.disabled = total >= limit;

      const addBox = card.querySelector('[data-ud-add]');
      if (addBox) addBox.disabled = total >= limit;

      if (count > 0) {
        summary.push(`${card.dataset.title} (${count})`);
        sum += Number(card.dataset.price) * count;
      }
    }

    for (const pack of this.packs) pack.setAttribute('aria-pressed', String(pack === this.pack));

    const label = this.querySelector('[data-ud-bar-label]');
    if (label) label.textContent = this.pack?.dataset.label || '';

    const price = this.querySelector('[data-ud-bar-price]');
    if (price) {
      price.textContent =
        this.dataset.barPrice === 'live' ? this.money.format(sum / 100) : this.pack?.dataset.priceFormatted || '';
    }

    // Compare-at is a property of the pack, so it tracks the selected pill
    // regardless of whether the bar price is the pack price or the live sum.
    const compare = this.querySelector('[data-ud-bar-compare]');
    if (compare) {
      const value = this.pack?.dataset.compare || '';
      compare.textContent = value;
      compare.hidden = !value;
    }

    const summaryNode = this.querySelector('[data-ud-summary]');
    if (summaryNode) summaryNode.textContent = summary.join(', ');

    if (this.cta) {
      const remaining = limit - total;
      const template = remaining === 1 ? this.cta.dataset.incompleteOne : this.cta.dataset.incomplete;
      this.ctaLabel.textContent =
        remaining > 0 ? (template || '').replace('{count}', String(remaining)) : this.cta.dataset.complete || '';
      this.cta.disabled = remaining !== 0;

      const counter = this.cta.querySelector('[data-ud-cta-count]');
      if (counter) counter.textContent = `${total}/${limit}`;
    }
  }

  async submit() {
    // Underscore-prefixed properties group the lines in the cart without showing to the shopper.
    const bundleId = `${Date.now().toString(36)}${Math.floor(Math.random() * 1e6).toString(36)}`;
    const properties = {
      _ud_bundle: bundleId,
      _ud_pack: this.pack?.dataset.label || '',
      // Numeric size so the cart can verify the group is complete rather than
      // trusting the label, which a merchant can rename at any time.
      _ud_pack_size: String(this.limit),
    };
    const items = [...this.qty].map(([id, quantity]) => ({ id: Number(id), quantity, properties }));
    if (!items.length || !this.cta) return;

    const itemCount = this.total;
    this.cta.disabled = true;
    this.ctaLabel.textContent = this.cta.dataset.adding || '';

    try {
      const sellerId = this.sellerId;
      if (sellerId) {
        await fetch(`${Theme.routes.cart_update_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { [this.dataset.sellerIdKey || 'Seller ID']: sellerId } }),
        });
      }

      const sections = [...document.querySelectorAll('cart-items-component')]
        .map((element) => element.dataset.sectionId)
        .filter(Boolean)
        .join(',');

      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify({ items, sections, sections_url: window.location.pathname }),
      });
      const result = await response.json();
      if (result.status) throw new Error(result.description || result.message);

      const cart = await (await fetch(`${Theme.routes.cart_url}.js`)).json();
      this.dispatchEvent(
        new CartAddEvent(cart, this.id, { source: 'ud-plp-bundle-grid', itemCount, sections: result.sections })
      );

      if (this.dataset.resetAfterAdd === 'true') this.qty.clear();
      this.render();

      if (this.dataset.afterAdd === 'cart') window.location.href = Theme.routes.cart_url;
      else if (this.dataset.afterAdd === 'drawer') document.querySelector('cart-drawer-component')?.open?.();
    } catch (error) {
      console.error(error);
      this.render();
    }
  }
}

if (!customElements.get('ud-plp-bundle-grid')) {
  customElements.define('ud-plp-bundle-grid', UdPlpBundleGrid);
}
